export const MM_PER_INCH = 25.4;
export const DISTANCE_FACTORS = { in: MM_PER_INCH, cm: 10, mm: 1 };
export const INTERVAL_FACTORS = { seconds: 1, minutes: 60, hours: 3600 };

export function numeric(value) {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const result = Number(value);
  return Number.isFinite(result) ? result : null;
}

function record(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function normalizeCommand(raw) {
  if (!record(raw) || !Number.isInteger(numeric(raw.id)) || numeric(raw.id) <= 0 ||
      typeof raw.command_name !== "string" || !raw.command_name.trim()) {
    throw new Error("The sensor command catalog contains an invalid command.");
  }
  if (raw.command_name === "restart") {
    return {
      ...raw, id: numeric(raw.id), fields: [], problem: "",
      sortOrder: numeric(raw.sort_order) ?? Number.MAX_SAFE_INTEGER,
    };
  }
  let variables = raw.input_variables;
  let problem = "";
  if (typeof variables === "string") {
    try {
      variables = JSON.parse(variables);
    } catch (error) {
      problem = "This command has invalid input definitions. Contact support.";
    }
  }
  if (variables == null) variables = {};
  if (!record(variables)) {
    variables = {};
    problem = "This command's input definitions are unsupported.";
  }
  const fields = Object.entries(variables).map(([key, cfg]) => {
    if (!record(cfg)) {
      problem = "This command has an invalid input definition.";
      return { key, type: "unsupported", label: key };
    }
    if (!["number", "text", "select", "ip"].includes(cfg.type)) {
      problem = `Unsupported input type for ${key}. Contact support.`;
    }
    if (cfg.type === "select" && (!Array.isArray(cfg.options) ||
        !cfg.options.length || cfg.options.some(option =>
          !record(option) || !["string", "number"].includes(typeof option.value) ||
          typeof option.label !== "string"))) {
      problem = `Invalid choices for ${key}. Contact support.`;
    }
    if (cfg.type === "select" && Array.isArray(cfg.options) &&
        new Set(cfg.options.map(option => String(option?.value))).size !== cfg.options.length) {
      problem = `Duplicate choices for ${key}. Contact support.`;
    }
    for (const bound of ["min", "max", "maxLength", "hex_bytes"]) {
      if (cfg[bound] != null && numeric(cfg[bound]) === null) {
        problem = `Invalid ${bound} for ${key}. Contact support.`;
      }
    }
    if (cfg.min != null && cfg.max != null && numeric(cfg.min) > numeric(cfg.max)) {
      problem = `Invalid range for ${key}. Contact support.`;
    }
    if (cfg.hex_bytes != null && (!Number.isInteger(numeric(cfg.hex_bytes)) ||
        numeric(cfg.hex_bytes) < 1 || numeric(cfg.hex_bytes) > 6)) {
      problem = `Unsupported encoded size for ${key}. Contact support.`;
    }
    if (cfg.maxLength != null && (!Number.isInteger(numeric(cfg.maxLength)) ||
        numeric(cfg.maxLength) < 1)) {
      problem = `Invalid text length for ${key}. Contact support.`;
    }
    return { ...cfg, key, label: cfg.label || key };
  });
  const placeholders = typeof raw.command_text === "string"
    ? [...raw.command_text.matchAll(/\{([A-Za-z_]\w*)\}/g)].map(match => match[1])
    : [];
  if (placeholders.some(key => !fields.some(field => field.key === key))) {
    problem = "This command is missing required input definitions. Contact support.";
  }
  return {
    ...raw, id: numeric(raw.id), fields, problem,
    sortOrder: numeric(raw.sort_order) ?? Number.MAX_SAFE_INTEGER,
  };
}

export function normalizeCatalog(data) {
  if (!Array.isArray(data)) throw new Error("The sensor catalog response is invalid.");
  console.log('data', data);
  const commands = data.map(normalizeCommand);
  console.log('commands', commands);
  if (new Set(commands.map(command => command.id)).size !== commands.length) {
    throw new Error("The sensor catalog contains duplicate command IDs.");
  }
  return commands.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

function matchesNumber(field, min, max, bytes) {
  return field?.type === "number" && numeric(field.min) === min &&
    numeric(field.max) === max && numeric(field.hex_bytes) === bytes &&
    field.required === true;
}

export function commandKind(command) {
  if (!command || command.problem) return "generic";
  if (command.id === 75 && command.command_type === "TDC" &&
      command.command_text === "01{value}" && command.fields.length === 1 &&
      command.fields[0].key === "value" &&
      /seconds/i.test(command.fields[0].label) &&
      matchesNumber(command.fields[0], 60, 86400, 3)) return "interval";
  if (command.id === 79 && command.command_type === "ALARM" &&
      command.command_text === "08{min1}{max1}00000000" &&
      command.fields.length === 2 &&
      ["min1", "max1"].every(key =>
        /\(mm\)/i.test(command.fields.find(field => field.key === key)?.label) &&
        matchesNumber(command.fields.find(field => field.key === key), 0, 65535, 2))) {
    return "alarms";
  }
  return "generic";
}

export function commandTitle(command) {
  const kind = commandKind(command);
  return kind === "alarms" ? "Tank full / empty alarms" :
    kind === "interval" ? "How often to send readings" : command.command_name;
}

export function initialValues(command) {
  return Object.fromEntries(command.fields.map(field => [
    field.key, field.default == null ? "" : String(field.default),
  ]));
}

export function calibrationGeometry(data) {
  if (!record(data)) return null;
  const sensor = numeric(data.sensor_height_offset);
  const full = numeric(data.scale_max_eng);
  const empty = numeric(data.scale_min_eng);
  if (data.sensor_mount_type !== "top" || data.invert_scale === false ||
      sensor === null || full === null || empty !== 0 ||
      sensor <= 0 || full <= 0 || full > sensor) return null;
  return { sensor, full };
}

export function calibrationFingerprint(data) {
  if (data == null) return "missing";
  return JSON.stringify([
    data.sensor_mount_type, numeric(data.sensor_height_offset),
    numeric(data.scale_min_eng), numeric(data.scale_max_eng), data.invert_scale,
    numeric(data.zero_point_offset), numeric(data.scale_factor),
  ]);
}

export function alarmDistance(amount, mode, unit, geometry) {
  const value = numeric(amount);
  if (value === null) throw new Error("Enter both alarm values.");
  if (value < 0) throw new Error("Alarm values cannot be negative.");
  const factor = DISTANCE_FACTORS[unit];
  if (!factor) throw new Error("Choose a distance unit.");
  if (!["percent", "height", "gap"].includes(mode)) throw new Error("Choose an input method.");
  if (mode !== "gap" && !geometry) {
    throw new Error("Saved top-mounted calibration is required for this input method.");
  }
  if (mode === "percent" && value > 100) throw new Error("Percent full must be 0 to 100.");
  const raw = mode === "percent" ? (geometry.sensor - value / 100 * geometry.full) * MM_PER_INCH :
    mode === "height" ? geometry.sensor * MM_PER_INCH - value * factor : value * factor;
  const rounded = Math.round(raw);
  if (raw < 0 || raw > 65535 || !Number.isFinite(raw)) {
    throw new Error("The converted gap must be between 0 and 65,535 mm.");
  }
  if (rounded === 0) {
    throw new Error("This becomes zero mm. Zero alarm behavior must be verified before sending.");
  }
  if (geometry && (raw > geometry.sensor * MM_PER_INCH + 1e-7 ||
      rounded > geometry.sensor * MM_PER_INCH + 1e-7)) {
    throw new Error("This gap would reach below the tank floor.");
  }
  return rounded;
}

export function alarmAmountsFromDistances(values, mode, unit, geometry) {
  if (!DISTANCE_FACTORS[unit] || !["percent", "height", "gap"].includes(mode)) {
    throw new Error("Choose a supported input method and unit.");
  }
  if (mode !== "gap" && !geometry) throw new Error("Calibration is required.");
  return Object.fromEntries(["min1", "max1"].map(key => {
    const mm = numeric(values[key]);
    if (mm === null) return [key, ""];
    const height = geometry ? geometry.sensor - mm / MM_PER_INCH : null;
    const value = mode === "percent" ? height / geometry.full * 100 :
      mode === "height" ? height * MM_PER_INCH / DISTANCE_FACTORS[unit] :
        mm / DISTANCE_FACTORS[unit];
    return [key, String(value)];
  }));
}

export function alarmPreview(draft, options, calibration) {
  const geometry = calibrationGeometry(calibration);
  const values = {};
  const errors = {};
  for (const key of ["min1", "max1"]) {
    try {
      values[key] = alarmDistance(draft[key], options.mode, options.unit, geometry);
    } catch (error) {
      values[key] = null;
      errors[key] = error.message;
    }
  }
  return { values, errors };
}

function genericValue(field, draft) {
  const blank = draft == null || String(draft).trim() === "";
  const required = field.required !== false;
  if (blank && required) throw new Error(`Enter ${field.label}.`);
  if (blank) return undefined;
  if (field.type === "number") {
    const value = numeric(draft);
    if (value === null) throw new Error(`${field.label} must be a number.`);
    if (field.min != null && value < numeric(field.min) ||
        field.max != null && value > numeric(field.max)) {
      throw new Error(`${field.label} is outside its allowed range.`);
    }
    if (field.hex_bytes != null && (!Number.isInteger(value) || value < 0 ||
        value > 2 ** (8 * numeric(field.hex_bytes)) - 1)) {
      throw new Error(`${field.label} does not fit the command's integer encoding.`);
    }
    return value;
  }
  if (field.type === "select") {
    const option = field.options.find(item => String(item.value) === String(draft));
    if (!option) throw new Error(`Choose a valid ${field.label}.`);
    return option.value;
  }
  if (field.type === "ip" && (!/^(0|[1-9]\d{0,2})(\.(0|[1-9]\d{0,2})){3}$/.test(String(draft)) ||
      String(draft).split(".").some(part => Number(part) > 255))) {
    throw new Error("Enter a valid IPv4 address.");
  }
  if (field.maxLength != null && String(draft).length > numeric(field.maxLength)) {
    throw new Error(`${field.label} is too long.`);
  }
  return String(draft);
}

export function buildValues(command, draft, options, calibration) {
  if (command.problem) throw new Error(command.problem);
  const kind = commandKind(command);
  if (kind === "alarms") {
    const preview = alarmPreview(draft, options, calibration);
    const error = preview.errors.min1 || preview.errors.max1;
    if (error) throw new Error(error);
    return preview.values;
  }
  if (kind === "interval") {
    const amount = numeric(draft.value);
    const factor = INTERVAL_FACTORS[options.intervalUnit];
    if (amount === null || amount <= 0 || !factor) throw new Error("Enter a reporting interval.");
    const seconds = amount * factor;
    // Tolerate floating-point conversion noise, not fractional seconds.
    if (Math.abs(seconds - Math.round(seconds)) > 1e-7) {
      throw new Error("The interval must convert to a whole number of seconds.");
    }
    return { value: genericValue(command.fields[0], String(Math.round(seconds))) };
  }
  return Object.fromEntries(command.fields.map(field =>
    [field.key, genericValue(field, draft[field.key])]).filter(([, value]) => value !== undefined));
}

export function alarmWarnings(values, geometry, dimensions) {
  const warnings = [];
  for (const key of ["min1", "max1"]) {
    const mm = numeric(values[key]);
    if (mm === null) continue;
    if (geometry && mm < (geometry.sensor - geometry.full) * MM_PER_INCH) {
      warnings.push(`${key === "min1" ? "High" : "Low"} alarm would be above the full/inlet mark.`);
    }
    if (!geometry && numeric(dimensions?.depth) > 0 &&
        mm > numeric(dimensions.depth) * MM_PER_INCH) {
      warnings.push("Gap exceeds tank depth. A riser may explain it; verify sensor position.");
    }
  }
  if (values.min1 >= values.max1) {
    warnings.push("High/low limits do not leave a normal range. Review both limits with support.");
  }
  return warnings;
}

export function readingFromRaw(raw) {
  const data = raw?.device_data;
  const result = { height: null, percent: null, timestamp: raw?.timestamp ?? null };
  if (!record(data) || raw.is_error) return result;
  result.height = numeric(data.inch_level_act ?? data.inch_level);
  const percent = numeric(data.percent_level);
  result.percent = percent !== null && percent >= 0 && percent <= 100 ? percent : null;
  if (result.height !== null && result.height < 0) result.height = null;
  return result;
}
